import angular from "angular";

import {
  Dates,
  Reports,
  Avatar,
  Translations,
  Employments,
  EnumerationsLogic,
  ODataQueryOptions,
  EmploymentListItem,
  HolidaysLogic,
  HolidayCode,
  HolidayCalculationDTO,
  AccountingHolidayPayDebtReportDataYear,
  AccountingHolidayPayDebtReportData,
  Numeric,
  HolidayYears,
  Arrays,
} from "@salaxy/core";
import { UiHelpers, ExcelHelpers, UiCrudHelpers } from "../../services";


/**
 * Component for displaying holiday report.
 */
export class HolidayReportController implements angular.IController {


  /** Ref date for the report. */
  public refDate = Dates.getToday();

  /** Current holiday years in the result. */
  public years: string[] = [];

  /** Current selected year. */
  public year: string | undefined;

  /** Result rows */
  public resultRows: ResultRow[] = [];

  /** Total holiday calculation */
  public holidayCalc: {[key: string]: HolidayCalculationDTO };

  /** Total of totals. */
  public holidayCalcTotal: {[key: string]: number };

  /**
   * Helper flag for UI. Indicates whether the debt has been queried.
   */
  public hasQueryedDebt = false;

  private data: AccountingHolidayPayDebtReportData;

  private employmentListItems: EmploymentListItem[] = [];


  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "HolidayYears",
    "Reports",
    "UiHelpers",
    "Employments",
    "UiCrudHelpers",
  ];

  constructor(private holidayYears: HolidayYears, private reports: Reports, private uiHelpers: UiHelpers, private employments: Employments, private uiCrudHelpers: UiCrudHelpers,) {
  }

  /** Controller initialization */
  public $onInit() {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    this.loadEmployments().then( employments => {
      this.employmentListItems = employments;
      this.query();
      loading.dismiss();
    });
  }

  /**
   * Shows an edit dialog for the employment.
   * @param row Row that contains field employmentId (Identifier for the employment).
   * @param row.employmentId The row must contain the employment identifier.
   */
  public showEditEmploymentDialog(row: ResultRow ): void {
    if (!row.employment?.id) {
      console.error("EmploymentId is missing", row);
      return;
    }
    this.uiCrudHelpers.openEditEmploymentDialog(row.employment?.id, "default", "holidays");
  }

  /** Query Holiday year data. */
  public query(queryDebt = false): Promise<AccountingHolidayPayDebtReportData> {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");

    const apiQuery = () : Promise<AccountingHolidayPayDebtReportData> => {
      if (queryDebt) {
        return this.reports.getAccountingHolidayPayDebtReportData(this.refDate);
      }
      else {
        return this.holidayYears.getForAllYears().then( (data) => ({
            holidayCalc: {
              totalAccruedHolidayPay : 0,
              totalAccruedHolidayBonus: 0,
              totalAccruedHolidaySocialSecurity: 0,
              totalAccruedHolidayPension: 0,
              totalAccruedHolidayUnemployment: 0,
              totalAccruedHolidayAccidentInsurance: 0,
              totalAccruedHolidayGroupLifeInsurance: 0,
            },
            holidayYears: data.map( (year) => ({
              holidayYear: year,
              holidayCalc: {}
            })),
          }));
      }
    };

    return  apiQuery().then(data => {
      this.data = data;
      this.holidayCalc = {};
      this.holidayCalc["total"] = data.holidayCalc;
      this.holidayCalcTotal = {};
      this.holidayCalcTotal["total"] = Numeric.round(
        (data.holidayCalc?.totalAccruedHolidayPay ?? 0) +
        (data.holidayCalc?.totalAccruedHolidayBonus  ?? 0) +
        (data.holidayCalc?.totalAccruedHolidaySocialSecurity  ?? 0) +
        (data.holidayCalc?.totalAccruedHolidayPension  ?? 0) +
        (data.holidayCalc?.totalAccruedHolidayUnemployment  ?? 0) +
        (data.holidayCalc?.totalAccruedHolidayAccidentInsurance  ?? 0) +
        (data.holidayCalc?.totalAccruedHolidayGroupLifeInsurance  ?? 0), 2);
      this.resultRows = [];
      this.years = [];
      this.year = undefined;
      this.data.holidayYears.forEach( (yearData) => {
        const resultRow: ResultRow = {
          employment: this.employmentListItems.find(x => x.id === yearData.holidayYear?.employmentId),
          data: yearData,
          holidayYearReportRow: this.getBlankHolidayYearReportRow(),
        };
        this.resultRows.push(resultRow);
        if (this.years.indexOf("" +yearData.holidayYear.year) < 0) {
          this.years.push("" +yearData.holidayYear.year);
        }
      });
      this.years.sort();
      this.years.reverse();
      this.year = this.years.length > 0 ? this.years[0]: undefined;
      for (const year of this.years) {
        const yearData = this.data.holidayYears.filter(x => x.holidayYear.year == Number.parseInt(year));
        const yearCalc = {
            totalAccruedHolidayPay: Numeric.round(Arrays.sum( yearData, (x) => x.holidayCalc?.totalAccruedHolidayPay ?? 0), 2),
            totalAccruedHolidayBonus: Numeric.round(Arrays.sum( yearData, (x) => x.holidayCalc?.totalAccruedHolidayBonus ?? 0), 2),
            totalAccruedHolidaySocialSecurity: Numeric.round(Arrays.sum( yearData, (x) => x.holidayCalc?.totalAccruedHolidaySocialSecurity ?? 0), 2),
            totalAccruedHolidayPension: Numeric.round(Arrays.sum( yearData, (x) => x.holidayCalc?.totalAccruedHolidayPension ?? 0), 2),
            totalAccruedHolidayUnemployment: Numeric.round(Arrays.sum( yearData, (x) => x.holidayCalc?.totalAccruedHolidayUnemployment ?? 0), 2),
            totalAccruedHolidayAccidentInsurance: Numeric.round(Arrays.sum( yearData, (x) => x.holidayCalc?.totalAccruedHolidayAccidentInsurance ?? 0), 2),
            totalAccruedHolidayGroupLifeInsurance: Numeric.round(Arrays.sum( yearData, (x) => x.holidayCalc?.totalAccruedHolidayGroupLifeInsurance ?? 0), 2),
          };
        this.holidayCalc[year] = yearCalc;
        this.holidayCalcTotal[year] = Numeric.round(
          (yearCalc.totalAccruedHolidayPay ?? 0) +
          (yearCalc.totalAccruedHolidayBonus  ?? 0) +
          (yearCalc.totalAccruedHolidayPension  ?? 0) +
          (yearCalc.totalAccruedHolidayUnemployment  ?? 0) +
          (yearCalc.totalAccruedHolidayAccidentInsurance  ?? 0) +
          (yearCalc.totalAccruedHolidayGroupLifeInsurance  ?? 0), 2);
      }

      this.createHolidayYearReportRows();
      if(queryDebt) {
        this.hasQueryedDebt = true;
      }

      loading.dismiss();
      return data;
    });
  }

  /**
   * Export totalRows to excel
   */
   public exportToExcel(){

    const year = Number.parseInt(this.year ?? "0");
    if (year === 0) {
      return;
    }

    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    const tableData = [];
    const headers = [
      Translations.get("SALAXY.NG1_Other.Roles.worker"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.table.startDate"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.table.endDate"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.table.employment"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.table.salary"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.table.holidayCode"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.table.periodDefaultAccrual"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.table.holidayPayCalculation"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.startSaldo"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.total"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.paid"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.available"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.holidaySalary"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.holidayCompensation"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.paidBonus"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.availableBonus"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.holidayBonus"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.holidayPayDailySalary"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.totalAccruedHolidayPay"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.totalAccruedHolidayBonus"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.totalAccruedHolidaySocialSecurity"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.totalAccruedHolidayPension"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.totalAccruedHolidayUnemployment"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.totalAccruedHolidayAccidentInsurance"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.totalAccruedHolidayGroupLifeInsurance"),
      Translations.get("SALAXY.NG1.HolidayReportComponent.holidayYearReportRow.totalAccruedHolidayTotal")
    ];
    tableData.push(headers);
    const rowData = this.resultRows.filter(x => x.data.holidayYear.year == year).map(x => {
      const row = x.holidayYearReportRow;
      return [
        row.workerAvatar.displayName,
        row.startDate = Dates.format(row.startDate),
        row.endDate = Dates.format(row.endDate),
        row.employmentType,
        row.grossSalary,
        row.holidayCode,
        row.periodDefaultAccrual,
        row.holidayPayCalculation,
        row.startSaldo,
        row.total,
        row.paid,
        row.available,
        row.holidaySalary,
        row.holidayCompensation,
        row.paidBonus,
        row.availableBonus,
        row.holidayBonus,
        row.holidayPayDailySalary,
        row.totalAccruedHolidayPay,
        row.totalAccruedHolidayBonus,
        row.totalAccruedHolidaySocialSecurity,
        row.totalAccruedHolidayPension,
        row.totalAccruedHolidayUnemployment,
        row.totalAccruedHolidayAccidentInsurance,
        row.totalAccruedHolidayGroupLifeInsurance,
        row.totalAccruedHolidayTotal
      ];
    });
    tableData.push(...rowData);

    if (tableData.length> 0) {
      ExcelHelpers.export(tableData, `holidayYear-${year}`).then(() => {
        loading.dismiss();
      });
    }
    else
    {
      loading.dismiss();
    }
  }

  private async loadEmployments(): Promise<EmploymentListItem[]> {
    const items: any[] = [];
    const loadItems = (nextPageLink: string = null): Promise<string> => {
      let options: ODataQueryOptions = null;
      const queryString = nextPageLink?.indexOf("?") ?? -1;
      if (queryString >= 0) {
        const searchParams = new URLSearchParams(nextPageLink.substr(queryString));
        if (searchParams.get("$skip")) {
          options = { $skip:  Number(searchParams.get("$skip"))};
        }
      }
      return this.employments.getOData(options).then((data) => {
        items.push(...data.items);
        return data.nextPageLink ? loadItems(data.nextPageLink) : null;
      });
    };

    return loadItems().then(() => {
      return items;
    });

  }

  private createHolidayYearReportRows(): void {
    this.resultRows.forEach(resultRow => {
      resultRow.holidayYearReportRow.workerAvatar = resultRow.employment?.otherPartyInfo?.avatar;
      resultRow.holidayYearReportRow.workerId = resultRow.employment?.otherPartyInfo?.avatar?.id;
      resultRow.holidayYearReportRow.employmentId = resultRow.employment?.id;
      resultRow.holidayYearReportRow.startDate = resultRow.employment?.data?.startDate;
      resultRow.holidayYearReportRow.endDate = resultRow.employment?.data?.endDate;
      resultRow.holidayYearReportRow.employmentType = EnumerationsLogic.getEnumLabel("EmploymentRelationType", resultRow.employment?.data?.type) === "#err EmploymentRelationType.NoEnumValue" ? "-" : EnumerationsLogic.getEnumLabel("EmploymentRelationType", resultRow.employment?.data?.type);
      resultRow.holidayYearReportRow.grossSalary =  Numeric.round(resultRow.employment?.grossSalary ?? 0, 2);
      resultRow.holidayYearReportRow.holidayCode = (resultRow.data.holidayYear.spec.code ?? HolidayCode.Undefined) == HolidayCode.Undefined ? "" : EnumerationsLogic.getEnumLabel("HolidayCode", resultRow.data.holidayYear.spec.code);
      resultRow.holidayYearReportRow.periodDefaultAccrual = (resultRow.data.holidayYear.spec.code == HolidayCode.Permanent14Days || resultRow.data.holidayYear.spec.code == HolidayCode.Permanent35Hours || resultRow.data.holidayYear.spec.code == HolidayCode.TemporaryTimeOff) ? resultRow.data.holidayYear.accrual.defaultAccrual : null;
      resultRow.holidayYearReportRow.holidayPayCalculation = (resultRow.data.holidayYear.spec.holidayPayCalculation ?? "undefined") == "undefined" ? "" : EnumerationsLogic.getEnumLabel("HolidayPayCalculation", resultRow.data.holidayYear.spec.holidayPayCalculation);
      resultRow.holidayYearReportRow.startSaldo = Numeric.round(resultRow.data.holidayYear.accrual.startSaldo ?? 0, 0);
      resultRow.holidayYearReportRow.total =  Numeric.round(resultRow.data.holidayYear.accrual.total ?? 0, 0);
      resultRow.holidayYearReportRow.paid =  Numeric.round(HolidaysLogic.getPaidHolidaysCalculation(resultRow.data.holidayYear, "total").holidayDays ?? 0, 0);
      resultRow.holidayYearReportRow.available =  Numeric.round(HolidaysLogic.getPaidHolidaysCalculation(resultRow.data.holidayYear, "endSaldo").holidayDays ?? 0, 0);
      resultRow.holidayYearReportRow.holidaySalary = HolidaysLogic.getPaidHolidaysCalculation(resultRow.data.holidayYear, "total").holidaySalary ?? 0;
      resultRow.holidayYearReportRow.holidayCompensation = HolidaysLogic.getPaidHolidaysCalculation(resultRow.data.holidayYear, "total").holidayCompensation ?? 0;
      resultRow.holidayYearReportRow.paidBonus =  Numeric.round(HolidaysLogic.getPaidBonusCalculation(resultRow.data.holidayYear, "total").holidayDays ?? 0, 0);
      resultRow.holidayYearReportRow.availableBonus =  Numeric.round(HolidaysLogic.getPaidBonusCalculation(resultRow.data.holidayYear, "endSaldo").holidayDays ?? 0, 0);
      resultRow.holidayYearReportRow.holidayBonus = HolidaysLogic.getPaidBonusCalculation(resultRow.data.holidayYear, "total").holidayBonus ?? 0;
      resultRow.holidayYearReportRow.holidayPayDailySalary = HolidaysLogic.getHolidayPayDailySalary(resultRow.data.holidayYear)?? 0;
      resultRow.holidayYearReportRow.totalAccruedHolidayPay = resultRow.data.holidayCalc?.totalAccruedHolidayPay ?? 0;
      resultRow.holidayYearReportRow.totalAccruedHolidayBonus = resultRow.data.holidayCalc?.totalAccruedHolidayBonus ?? 0;
      resultRow.holidayYearReportRow.totalAccruedHolidaySocialSecurity = resultRow.data.holidayCalc?.totalAccruedHolidaySocialSecurity ?? 0;
      resultRow.holidayYearReportRow.totalAccruedHolidayPension = resultRow.data.holidayCalc?.totalAccruedHolidayPension ?? 0;
      resultRow.holidayYearReportRow.totalAccruedHolidayUnemployment = resultRow.data.holidayCalc?.totalAccruedHolidayUnemployment ?? 0;
      resultRow.holidayYearReportRow.totalAccruedHolidayAccidentInsurance = resultRow.data.holidayCalc?.totalAccruedHolidayAccidentInsurance ?? 0;
      resultRow.holidayYearReportRow.totalAccruedHolidayGroupLifeInsurance = resultRow.data.holidayCalc?.totalAccruedHolidayGroupLifeInsurance ?? 0;
      resultRow.holidayYearReportRow.totalAccruedHolidayTotal = Numeric.round(
      resultRow.holidayYearReportRow.totalAccruedHolidayPay +
      resultRow.holidayYearReportRow.totalAccruedHolidayBonus +
      resultRow.holidayYearReportRow.totalAccruedHolidaySocialSecurity +
      resultRow.holidayYearReportRow.totalAccruedHolidayPension +
      resultRow.holidayYearReportRow.totalAccruedHolidayUnemployment +
      resultRow.holidayYearReportRow.totalAccruedHolidayAccidentInsurance +
      resultRow.holidayYearReportRow.totalAccruedHolidayGroupLifeInsurance, 2);
    });
  }
  private getBlankHolidayYearReportRow(): HolidayYearReportRow {
    return {
      workerAvatar: null,
      workerId: null,
      employmentId: null,
      startDate: null,
      endDate: null,
      employmentType: null,
      grossSalary: 0,
      holidayCode: null,
      periodDefaultAccrual: null,
      holidayPayCalculation: null,
      startSaldo: 0,
      total: 0,
      paid: 0,
      available: 0,
      holidaySalary: 0,
      holidayCompensation: 0,
      paidBonus: 0,
      availableBonus: 0,
      holidayBonus: 0,
      holidayPayDailySalary: 0,
      totalAccruedHolidayPay: 0,
      totalAccruedHolidayBonus: 0,
      totalAccruedHolidaySocialSecurity: 0,
      totalAccruedHolidayPension: 0,
      totalAccruedHolidayUnemployment: 0,
      totalAccruedHolidayAccidentInsurance: 0,
      totalAccruedHolidayGroupLifeInsurance: 0,
      totalAccruedHolidayTotal: 0,
    };
  }
}

interface ResultRow {
  employment: EmploymentListItem;
  data: AccountingHolidayPayDebtReportDataYear;
  holidayYearReportRow: HolidayYearReportRow;
}

interface HolidayYearReportRow {
  workerAvatar: Avatar;
  workerId: string;
  employmentId: string;
  startDate: string;
  endDate: string;
  employmentType: string;
  grossSalary: number;
  holidayCode: string;
  periodDefaultAccrual: number;
  holidayPayCalculation: string;
  startSaldo: number;
  total: number;
  paid: number;
  available: number;
  holidaySalary: number;
  holidayCompensation: number;
  paidBonus: number;
  availableBonus: number;
  holidayBonus: number;
  holidayPayDailySalary: number;

  totalAccruedHolidayPay: number;
  /** Total sum of accrued holiday pays. */
  totalAccruedHolidayBonus: number;
  /** Total sum of social security payments for accrued holiday pays. */
  totalAccruedHolidaySocialSecurity: number;
  /** Total sum of pension payments for accrued holiday pays. */
  totalAccruedHolidayPension: number;
  /** Total sum of unemployment insurance payments for accrued holiday pays. */
  totalAccruedHolidayUnemployment: number;
  /** Total accident insurance. */
  totalAccruedHolidayAccidentInsurance: number;
  /** Total accident insurance. */
  totalAccruedHolidayGroupLifeInsurance: number;
  totalAccruedHolidayTotal: number;

}